import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import ThemeToggle from '../components/ThemeToggle';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3005';

const Unsubscribe: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const hasFired = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Link de descadastro inválido. Nenhum token fornecido.');
      return;
    }

    if (hasFired.current) return;

    const unsubscribe = async () => {
      if (hasFired.current) return;
      hasFired.current = true;

      try {
        const response = await axios.get(`${API_URL}/api/users/unsubscribe`, {
          params: { token },
        });

        if (response.data?.success) {
          setStatus('success');
          setMessage('Você foi descadastrado com sucesso. Não receberá mais emails de lembrete da Upmindy.');
        } else {
          setStatus('error');
          setMessage('Token inválido ou expirado. Tente novamente pelo link de um email mais recente.');
        }
      } catch {
        setStatus('error');
        setMessage('Ocorreu um erro ao processar sua solicitação. Por favor, tente novamente.');
      }
    };

    unsubscribe();
  }, [searchParams]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4">
        <div className="absolute top-4 right-4 z-10">
          <ThemeToggle />
        </div>
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="flex justify-center">
            <svg className="animate-spin h-10 w-10 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <p className="text-gray-600 dark:text-gray-400">Processando sua solicitação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
            Descadastro de Emails
          </h2>

          <div className={`mt-8 p-6 rounded-lg ${
            status === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700'
          }`}>
            <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
              status === 'success' ? 'bg-green-100 dark:bg-green-800' : 'bg-red-100 dark:bg-red-800'
            } mb-4`}>
              {status === 'success' ? (
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>

            <p className={`text-sm ${
              status === 'success'
                ? 'text-green-800 dark:text-green-300'
                : 'text-red-800 dark:text-red-300'
            }`}>
              {message}
            </p>
          </div>

          {status === 'success' && (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Caso mude de ideia, você pode reativar as notificações nas configurações do seu perfil.
            </p>
          )}

          <div className="mt-6 space-y-3">
            <Link
              to="/login"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Ir para o Login
            </Link>
          </div>
        </div>

        <div className="mt-8 text-center">
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/terms-of-service" className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
              Termos de Uso
            </Link>
            <span className="hidden sm:inline">•</span>
            <Link to="/privacy-policy" className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
              Política de Privacidade
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
